import { useSyncExternalStore } from "react";
import { useSessionIfAny, type Me } from "./session";

// Whether a vertical's presentation shows (ADR-0489; #2072): its words, its
// icons, its page order and its Overview default follow the selected site only
// for a person who turned them on, and always for a person who works inside
// one customer's estate. The preference is a person's own and sticks in this
// browser, as the theme does (decision 1a). A screen's words, icons and page
// order read it through `lexicon.ts`, which answers no vertical while it is off
// (3a); the Overview reads it to ask for the neutral layout (5a), and the
// account menu to draw the switch.
const KEY = "zenith.verticals";
const listeners = new Set<() => void>();

/** Off by default; a browser whose storage cannot be read reads off. */
function chosen(): boolean {
  try { return localStorage.getItem(KEY) === "on"; } catch { return false; }
}

/** Turns the preference on or off in this browser and tells every reader. A
 * write the browser refuses changes nothing, so the switch stays at off. */
export function chooseVerticals(on: boolean): void {
  try { if (on) localStorage.setItem(KEY, "on"); else localStorage.removeItem(KEY); } catch { /* ignore */ }
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  // Another tab of this browser turned it on or off.
  window.addEventListener("storage", l);
  return () => { listeners.delete(l); window.removeEventListener("storage", l); };
}

/** Decision 2a: a person every one of whose memberships is at a customer or a
 * site always reads the vertical and is offered no switch. A superuser and a
 * break-glass session cross every vertical and are offered it, as is anyone
 * holding an organization's membership. **This is not authorization**: it
 * chooses which preference is offered and reaches nothing, so it reads the
 * memberships' scopes and walks no tree (§6.2). */
export function alwaysShowsVerticals(me: Me | undefined): boolean {
  if (!me || me.user.superuser || me.break_glass) return false;
  return me.memberships.length > 0 && me.memberships.every((m) => m.scope_type === "customer" || m.scope_type === "site");
}

/** The switch's own state, for the account menu. */
export function useVerticalsChosen(): boolean {
  return useSyncExternalStore(subscribe, chosen, () => false);
}

/** Whether this person's screens show a vertical at all. */
export function useShowsVerticals(): boolean {
  const me = useSessionIfAny()?.me;
  const on = useVerticalsChosen();
  return alwaysShowsVerticals(me) || on;
}
